import { useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";

import { cn, getText, type LocalisationConfig } from "shared/lib";
import {
  applicationPageVar,
  invalidAddressTypeVar,
  Pages,
  Sections,
  wizardNavigatorVar,
} from "@/variables/globalVar";

import { useMediaQuery } from "shared/hooks";
import { useAccount } from "@/hooks";

import {
  AddressContainer,
  type AddressContainerAPI,
} from "@/components/address";
import {
  InvalidAddress,
  AddressError,
} from "@/components/address/invalidAddress";

export function AddressPage() {
  const wizardNavigator = useAtomValue(wizardNavigatorVar);
  const setApplicationPageVar = useSetAtom(applicationPageVar);
  const invalidAddressType = useAtomValue(invalidAddressTypeVar);

  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addressContainerRef = useRef<AddressContainerAPI>(null);

  const { application, conversation } = useAccount();

  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  useEffect(() => {
    if (conversation?.address_data) {
      setApplicationPageVar(Pages.App);
    }
  }, [conversation]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className={cn(
        "flex grow flex-col overflow-auto px-5 py-5 md:pt-2",
        !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
      )}
    >
      <div
        className={cn(
          "flex grow flex-col items-center rounded-xl",
          md ? "bg-interfaceBgLightPaper" : "",
        )}
      >
        {wizardNavigator === Sections.Address && (
          <>
            <div className="grow" />

            <div className="flex w-full flex-col items-start overflow-visible md:max-w-[680px]">
              <h1 className="text-textPrimary text-headingxl md:text-heading3xl pb-2">
                {getText("Let's start with an address")}
              </h1>

              <p className="text-textPrimary text-bodylg pb-5">
                {getText(
                  "We'll use the address to determine the lot's size, zone and planning overlays.",
                )}
              </p>

              <p className="text-textSubdued text-bodysm md:text-bodymd pb-5 md:pb-8">
                {getText(
                  "☝️This service can't assist with existing permits and some other topics. If it can’t assist, it will point you to the best place to get the information you need.",
                )}
              </p>

              <AddressContainer
                onSelectedAddress={setSelectedAddressId}
                onSubmitAddressCompleted={() => setIsSubmitting(false)}
                onSubmitAddressFailed={() => setIsSubmitting(false)}
                placeholder={getText("Enter an address")}
                ref={addressContainerRef}
              />
            </div>

            <div className="grow" />
          </>
        )}

        {wizardNavigator === Sections.InvalidAddress && (
          <div className="relative flex min-h-full grow flex-col">
            <section className="flex grow flex-col overflow-y-auto md:m-10">
              <InvalidAddress
                address={application?.address}
                localisationConfig={
                  application?.council?.localisation_config_value as
                    | LocalisationConfig
                    | undefined
                }
                mode={invalidAddressType}
              />
            </section>
          </div>
        )}

        {wizardNavigator === Sections.AddressError && (
          <div className="relative flex min-h-full grow flex-col">
            <section className="flex grow flex-col overflow-y-auto md:m-10">
              <AddressError />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
